/**
 * Random identifier generation.
 *
 * `crypto.randomUUID()` is exposed **only in secure contexts**. A page opened
 * over plain HTTP — a phone on the LAN hitting `http://192.168.1.10:8080`,
 * for example — sees `crypto.randomUUID === undefined` and the call throws
 * `crypto.randomUUID is not a function`, while the same build on
 * `localhost` or HTTPS works. `crypto.getRandomValues()` has no such
 * restriction, so it backs the fallback.
 */

const HEX = '0123456789abcdef';

/** RFC 4122 version-4 UUID, shaped exactly like `crypto.randomUUID()`. */
function fallbackUuid(): string {
  const bytes = new Uint8Array(16);
  const webCrypto = globalThis.crypto;

  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    // Last resort for browsers exposing no Web Crypto at all.
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  // Stamp version 4 and the RFC 4122 variant so the shape stays valid.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  let uuid = '';
  for (let index = 0; index < bytes.length; index += 1) {
    if (index === 4 || index === 6 || index === 8 || index === 10) uuid += '-';
    uuid += HEX[bytes[index] >> 4] + HEX[bytes[index] & 0x0f];
  }

  return uuid;
}

/**
 * A unique identifier for React keys, toast ids and similar client-side use.
 *
 * Prefers the native `crypto.randomUUID()` and degrades gracefully wherever
 * it is unavailable (insecure origins, older WebViews).
 */
export function randomId(): string {
  const webCrypto = globalThis.crypto;

  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  return fallbackUuid();
}
